// src/components/ModalClienteRapido.jsx
import React, { useState } from "react";
import { Plus } from "lucide-react";
import {
  getFirestore,
  collection,
  addDoc,
  Timestamp,
} from "firebase/firestore";

const initialState = {
  tipoPessoa: "pf", // 'pf' | 'pj'
  nomeRazao: "",
  cpfCnpj: "",
  ie: "",
  email: "",
  telefone: "",
  cep: "",
  logradouro: "",
  numero: "",
  complemento: "",
  bairro: "",
  cidade: "",
  uf: "",
};

export default function ModalClienteRapido({
  open,
  onClose,
  onSaved,          // (clienteCriado) => void
  empresaId: propEmpresaId, // opcional (padrão: localStorage)
}) {
  const [form, setForm] = useState(initialState);
  const [saving, setSaving] = useState(false);

  if (!open) return null;

  const handleChange = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const onlyDigits = (s) => (s || "").replace(/\D/g, "");

  const validar = () => {
    const e = {};
    if (!form.nomeRazao.trim()) e.nomeRazao = "Informe o nome/razão social";
    if (!form.cpfCnpj.trim()) e.cpfCnpj = "Informe CPF/CNPJ";
    if (!form.cep.trim()) e.cep = "Informe o CEP";
    if (!form.logradouro.trim()) e.logradouro = "Informe o logradouro";
    if (!form.numero.trim()) e.numero = "Informe o número";
    if (!form.bairro.trim()) e.bairro = "Informe o bairro";
    if (!form.cidade.trim()) e.cidade = "Informe a cidade";
    if (!form.uf.trim()) e.uf = "Informe a UF";
    return e;
  };

  const buscarCEP = async () => {
    const cepNum = onlyDigits(form.cep);
    if (cepNum.length !== 8) return;
    try {
      const r = await fetch(`https://viacep.com.br/ws/${cepNum}/json/`);
      const j = await r.json();
      if (!j.erro) {
        setForm((f) => ({
          ...f,
          logradouro: j.logradouro || f.logradouro,
          bairro: j.bairro || f.bairro,
          cidade: j.localidade || f.cidade,
          uf: j.uf || f.uf,
        }));
      }
    } catch {}
  };

  const handleSalvar = async () => {
    const err = validar();
    if (Object.keys(err).length) {
      alert(Object.values(err)[0]);
      return;
    }

    const payload = {
      tipoPessoa: form.tipoPessoa,
      nome: form.nomeRazao,
      razaoSocial: form.tipoPessoa === "pj" ? form.nomeRazao : "",
      cpf: form.tipoPessoa === "pf" ? onlyDigits(form.cpfCnpj) : "",
      cnpj: form.tipoPessoa === "pj" ? onlyDigits(form.cpfCnpj) : "",
      ie: form.tipoPessoa === "pj" ? form.ie : "",
      email: form.email || "",
      telefone: form.telefone || "",
      endereco: {
        cep: onlyDigits(form.cep),
        logradouro: form.logradouro,
        numero: form.numero,
        complemento: form.complemento || "",
        bairro: form.bairro,
        cidade: form.cidade,
        uf: form.uf,
      },
      origem: "balcao",
      criadoEm: Timestamp.now(),
    };

    setSaving(true);
    const empresaId = propEmpresaId || localStorage.getItem("empresaId");
    let created = { id: `temp-${Date.now()}`, ...payload };

    try {
      if (empresaId) {
        const db = getFirestore();
        const col = collection(db, "empresas", empresaId, "clientes");
        const docRef = await addDoc(col, payload);
        created = { id: docRef.id, ...payload };
      }
      onSaved && onSaved(created);
      onClose && onClose();
    } catch (err) {
      console.error("Erro ao salvar cliente rápido:", err);
      alert("Não foi possível salvar o cliente.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-[2px] animate-fadein">
      <div className="bg-white dark:bg-gray-900 rounded-3xl shadow-2xl w-full max-w-2xl relative animate-modalSlideIn overflow-hidden">
        <div className="max-h-[94vh] flex flex-col">
          {/* Header */}
          <div className="p-7 pb-0 relative">
            <button
              className="absolute right-8 top-7 text-gray-500 hover:text-[#004072] dark:text-gray-200 dark:hover:text-white text-base font-medium"
              onClick={onClose}
            >
              Fechar
            </button>
            <h2 className="text-2xl font-bold mb-6 text-[#004072] dark:text-white">
              Cadastro rápido de cliente
            </h2>
          </div>

          {/* Body */}
          <div className="px-7 pb-6 space-y-6 overflow-y-auto flex-1">
            {/* Tipo */}
            <div className="flex gap-3">
              <button
                type="button"
                className={`px-4 py-2 rounded-xl border ${
                  form.tipoPessoa === "pf"
                    ? "bg-blue-600 text-white border-blue-600"
                    : "bg-gray-100 dark:bg-gray-800 dark:text-white dark:border-gray-700"
                }`}
                onClick={() => handleChange("tipoPessoa", "pf")}
              >
                Pessoa Física
              </button>
              <button
                type="button"
                className={`px-4 py-2 rounded-xl border ${
                  form.tipoPessoa === "pj"
                    ? "bg-blue-600 text-white border-blue-600"
                    : "bg-gray-100 dark:bg-gray-800 dark:text-white dark:border-gray-700"
                }`}
                onClick={() => handleChange("tipoPessoa", "pj")}
              >
                Pessoa Jurídica
              </button>
            </div>

            {/* Identificação */}
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-semibold mb-1 dark:text-gray-200">
                  {form.tipoPessoa === "pj" ? "Razão Social" : "Nome completo"}
                </label>
                <input
                  className="border rounded-xl px-4 py-3 w-full bg-gray-50 dark:bg-gray-800 dark:text-white dark:border-gray-700 focus:ring-2 focus:ring-[#004AAD]"
                  value={form.nomeRazao}
                  onChange={(e) => handleChange("nomeRazao", e.target.value)}
                  placeholder={form.tipoPessoa === "pj" ? "Ex: ACME LTDA" : "Ex: João da Silva"}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-1 dark:text-gray-200">
                  {form.tipoPessoa === "pj" ? "CNPJ" : "CPF"}
                </label>
                <input
                  className="border rounded-xl px-4 py-3 w-full bg-gray-50 dark:bg-gray-800 dark:text-white dark:border-gray-700 focus:ring-2 focus:ring-[#004AAD]"
                  value={form.cpfCnpj}
                  onChange={(e) => handleChange("cpfCnpj", e.target.value)}
                  placeholder={form.tipoPessoa === "pj" ? "00.000.000/0000-00" : "000.000.000-00"}
                />
              </div>

              {form.tipoPessoa === "pj" && (
                <div>
                  <label className="block text-sm font-semibold mb-1 dark:text-gray-200">Inscrição Estadual (opcional)</label>
                  <input
                    className="border rounded-xl px-4 py-3 w-full bg-gray-50 dark:bg-gray-800 dark:text-white dark:border-gray-700 focus:ring-2 focus:ring-[#004AAD]"
                    value={form.ie}
                    onChange={(e) => handleChange("ie", e.target.value)}
                    placeholder="Isento ou número da IE"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-semibold mb-1 dark:text-gray-200">E-mail (opcional)</label>
                <input
                  className="border rounded-xl px-4 py-3 w-full bg-gray-50 dark:bg-gray-800 dark:text-white dark:border-gray-700 focus:ring-2 focus:ring-[#004AAD]"
                  value={form.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  placeholder="exemplo@dominio.com"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-1 dark:text-gray-200">Telefone (opcional)</label>
                <input
                  className="border rounded-xl px-4 py-3 w-full bg-gray-50 dark:bg-gray-800 dark:text-white dark:border-gray-700 focus:ring-2 focus:ring-[#004AAD]"
                  value={form.telefone}
                  onChange={(e) => handleChange("telefone", e.target.value)}
                  placeholder="(00) 00000-0000"
                />
              </div>
            </div>

            {/* Endereço */}
            <div className="grid md:grid-cols-4 gap-5">
              {/* CEP mais largo */}
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold mb-1 dark:text-gray-200">CEP</label>
                <div className="flex gap-3">
                  <input
                    className="flex-1 border rounded-xl px-4 py-3 bg-gray-50 dark:bg-gray-800 dark:text-white dark:border-gray-700 focus:ring-2 focus:ring-[#004AAD]"
                    value={form.cep}
                    onChange={(e) => handleChange("cep", e.target.value)}
                    onBlur={buscarCEP}
                    placeholder="00000-000"
                    inputMode="numeric"
                    maxLength={9}
                  />
                  <button
                    type="button"
                    onClick={buscarCEP}
                    className="px-4 py-3 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
                  >
                    Buscar
                  </button>
                </div>
              </div>

              {/* Logradouro um pouco menor */}
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold mb-1 dark:text-gray-200">Logradouro</label>
                <input
                  className="border rounded-xl px-4 py-3 w-full bg-gray-50 dark:bg-gray-800 dark:text-white dark:border-gray-700 focus:ring-2 focus:ring-[#004AAD]"
                  value={form.logradouro}
                  onChange={(e) => handleChange("logradouro", e.target.value)}
                  placeholder="Rua, avenida..."
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-1 dark:text-gray-200">Número</label>
                <input
                  className="border rounded-xl px-4 py-3 w-full bg-gray-50 dark:bg-gray-800 dark:text-white dark:border-gray-700 focus:ring-2 focus:ring-[#004AAD]"
                  value={form.numero}
                  onChange={(e) => handleChange("numero", e.target.value)}
                  placeholder="123"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-semibold mb-1 dark:text-gray-200">Complemento (opcional)</label>
                <input
                  className="border rounded-xl px-4 py-3 w-full bg-gray-50 dark:bg-gray-800 dark:text-white dark:border-gray-700 focus:ring-2 focus:ring-[#004AAD]"
                  value={form.complemento}
                  onChange={(e) => handleChange("complemento", e.target.value)}
                  placeholder="Apto, bloco..."
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-1 dark:text-gray-200">Bairro</label>
                <input
                  className="border rounded-xl px-4 py-3 w-full bg-gray-50 dark:bg-gray-800 dark:text-white dark:border-gray-700 focus:ring-2 focus:ring-[#004AAD]"
                  value={form.bairro}
                  onChange={(e) => handleChange("bairro", e.target.value)}
                  placeholder="Centro"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-1 dark:text-gray-200">Cidade</label>
                <input
                  className="border rounded-xl px-4 py-3 w-full bg-gray-50 dark:bg-gray-800 dark:text-white dark:border-gray-700 focus:ring-2 focus:ring-[#004AAD]"
                  value={form.cidade}
                  onChange={(e) => handleChange("cidade", e.target.value)}
                  placeholder="São Paulo"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-1 dark:text-gray-200">UF</label>
                <input
                  className="border rounded-xl px-4 py-3 w-full bg-gray-50 dark:bg-gray-800 dark:text-white dark:border-gray-700 focus:ring-2 focus:ring-[#004AAD]"
                  value={form.uf}
                  onChange={(e) => handleChange("uf", e.target.value.toUpperCase().slice(0,2))}
                  placeholder="SP"
                  maxLength={2}
                />
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-7 py-4 bg-white/95 dark:bg-gray-900/95 backdrop-blur border-t dark:border-gray-800 flex justify-end gap-3 rounded-b-3xl">
            <button
              onClick={onClose}
              className="px-5 py-3 rounded-lg border border-gray-300 dark:border-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 font-semibold transition"
              disabled={saving}
            >
              Cancelar
            </button>
            <button
              onClick={handleSalvar}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-5 py-3 font-semibold text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-60"
              disabled={saving}
            >
              <Plus size={18} /> {saving ? "Salvando..." : "Salvar cliente"}
            </button>
          </div>
        </div>

        <style>{`
          .animate-modalSlideIn { animation: modalSlideIn 0.35s cubic-bezier(.32,.72,.57,1) both; }
          @keyframes modalSlideIn {
            0% { opacity:0; transform: translateY(60px) scale(.97); }
            100% { opacity:1; transform: translateY(0) scale(1); }
          }
          .animate-fadein { animation: fadein 0.25s; }
          @keyframes fadein { from { opacity: 0; } to { opacity: 1; } }
        `}</style>
      </div>
    </div>
  );
}
